// AI Generated Component
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    computed,
    inject,
    Input,
    model,
    OnInit,
} from '@angular/core';
import { MonDialog } from '@client/app/modules/dialog-upgrade-wrapper/mon-dialog.service';
import { DateTimeService } from '@client/app/services/date-time/date-time.service';
import { UptimeRepoService } from '@client/app/services/api/uptime-repo/uptime-repo.service';
import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { ngbDateStructFromNativeDate } from '@client/vendors/ngb-adapters';
import { ISOTimeString } from '@client/types/time.type';
import { safeClone } from '@client/app/shared/utils/object-utils';

type FormScheduleDownTimeModel = {
    id?: number,
    uptime_id: number,
    recurring: 'no' | 'daily' | 'weekly' | 'monthly',
    description: string,
    register_downtime: boolean,
    start_at: ISOTimeString,
    end_at: ISOTimeString,
    'current_start_at': ISOTimeString,
    'current_end_at': ISOTimeString,
    'created_at': ISOTimeString,
    'updated_at': ISOTimeString
};

@Component({
    selector: 'form-uptime-scheduled-downtime',
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
        <form class="scheduled-downtime-form form-horizontal"
              #scheduledForm="ngForm"
              (ngSubmit)="scheduledForm.valid && submit()">
            <div class="mon-dialog-header">
                @if (scheduledDowntime.id) {
                    <page-header [header]="'Edit exemption' | translate" [subHeader]="'Edit your exemption' | translate" />
                }
                @else {
                    <page-header [header]="'Add exemption' | translate" [subHeader]="'Add a new exemption to your heartbeat check' | translate" />
                }
            </div>
            <div class="mon-dialog-body-with-footer bg-body">
                <div class="card panel-monsido-card mt-4 mx-4">
                    <div class="card-header">
                        <h3 class="font-weight-300" translate>Exemptions</h3>
                    </div>
                    <div class="card-body">
                        <div class="row">
                            <div class="col-48">
                                <span translate>Add timeslots where you won't receive notifications on downtime and slow responsetimes.</span>
                                <span translate>This is useful if you have defined timeslots for maintenance.</span>
                            </div>
                            <div class="col-48 mt-4">
                                <fieldset name="time"
                                          class="form-group my-6">
                                    <legend class="mb-4"
                                            translate>Exemption time
                                    </legend>
                                    <form-field-select name="recurring"
                                                       *ngIf="loaded"
                                                       [(ngModel)]="scheduledDowntime.recurring"
                                                       (ngChange)="onChangeRecurringOrStartDate()"
                                                       [options]="recurringOptions"
                                                       [required]="true"
                                                       [returnValue]="'value'"
                                                       [label]="'Repeat' | translate">
                                    </form-field-select>

                                    <div class="form-group row">
                                        <label class="col-form-label text-bold text-right col-sm-8"
                                               translate>Start date</label>
                                        <div class="col-sm-20 col-24">
                                            <div class="input-group">
                                                <div class="input-group-prepend">
                                                    <button type="button"
                                                            class="btn btn-border-radius btn-secondary"
                                                            (click)="startDateDatePicker.toggle()">
                                                        <span class="fas fa-calendar-alt"
                                                              aria-hidden="true"></span>
                                                    </button>
                                                </div>
                                                <input name="startDate"
                                                       class="form-control datepicker-field"
                                                       ngbDatepicker
                                                       #startDateDatePicker="ngbDatepicker"
                                                       [(ngModel)]="formScheduledDowntimeStartAtDate"
                                                       (ngModelChange)="onChangeRecurringOrStartDate()"
                                                />
                                            </div>
                                        </div>
                                        <div class="col-sm-20 col-24">
                                            <ngb-timepicker [(ngModel)]="formScheduledDowntimeStartAtTime"
                                                            name="scheduledDownTimeStartAt"
                                                            [meridian]="true"
                                                            [spinners]="false"
                                                            [minuteStep]="5" />
                                        </div>
                                    </div>

                                    <div class="form-group row">
                                        <label class="col-form-label text-bold text-right col-sm-8"
                                               for="scheduled_downtime_end_at"
                                               translate>End date</label>
                                        <div class="col-sm-20 col-24">
                                            <div class="input-group">
                                                @if (endDateOptions.minDate && endDateOptions.maxDate) {
                                                    <div class="input-group-prepend">
                                                        <button type="button"
                                                                class="btn btn-border-radius btn-secondary"
                                                                (click)="endDateDatePicker.toggle()">
                                                        <span class="fas fa-calendar-alt"
                                                              aria-hidden="true"></span>
                                                        </button>
                                                    </div>
                                                    <input type="text"
                                                           name="endDate"
                                                           class="form-control datepicker-field"
                                                           id="scheduled_downtime_end_at"
                                                           ngbDatepicker
                                                           [minDate]="endDateOptions.minDate"
                                                           [maxDate]="endDateOptions.maxDate"
                                                           #endDateDatePicker="ngbDatepicker"
                                                           [(ngModel)]="formScheduledDowntimeEndAtDate"
                                                    />
                                                }
                                                @else {
                                                    <div class="input-group-prepend">
                                                        <button type="button"
                                                                class="btn btn-border-radius btn-secondary"
                                                                (click)="endDateDatePicker.toggle()">
                                                        <span class="fas fa-calendar-alt"
                                                              aria-hidden="true"></span>
                                                        </button>
                                                    </div>
                                                    <input type="text"
                                                           name="endDate"
                                                           class="form-control datepicker-field"
                                                           id="scheduled_downtime_end_at"
                                                           ngbDatepicker
                                                           #endDateDatePicker="ngbDatepicker"
                                                           [(ngModel)]="formScheduledDowntimeEndAtDate"
                                                    />
                                                }
                                            </div>
                                        </div>
                                        <div class="col-sm-20 col-24">
                                            <ngb-timepicker [(ngModel)]="formScheduledDowntimeEndAtTime"
                                                            name="scheduledDownTimeEndAt"
                                                            [meridian]="true"
                                                            [spinners]="false"
                                                            [minuteStep]="5" />
                                        </div>
                                    </div>
                                </fieldset>
                                <fieldset name="options"
                                          class="form-group my-6">
                                    <legend class="mb-4"
                                            translate>Exemption options
                                    </legend>
                                    <div class="form-group row">
                                        <div class="col-40 offset-8">
                                            <div class="form-check">
                                                <input type="radio"
                                                       class="form-check-input"
                                                       name="register_downtime"
                                                       id="register_downtime_true"
                                                       [value]="false"
                                                       [(ngModel)]="scheduledDowntime.register_downtime"
                                                />
                                                <label for="register_downtime_true"
                                                       class="form-check-label text-grey font-weight-400"
                                                       translate>
                                                    Exemption time is <strong>not</strong> part of the downtime
                                                    calculation<br>
                                                    If you have no other downtimes, your uptime will be shown as 100%
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="form-group row">
                                        <div class="col-40 offset-8">
                                            <div class="form-check">
                                                <input type="radio"
                                                       class="form-check-input"
                                                       name="register_downtime"
                                                       id="register_downtime_false"
                                                       [value]="true"
                                                       [(ngModel)]="scheduledDowntime.register_downtime"
                                                />
                                                <label for="register_downtime_false"
                                                       class="form-check-label text-grey font-weight-400">
                                                    Exemption time <strong>is</strong>
                                                    part of the downtime calculation<br>
                                                    (If you have 1 hour exemption time and no other downtimes in one
                                                    day,
                                                    your uptime will be shown as 95.83% uptime)
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </fieldset>
                                <div class="row mt-6">
                                    <div class="col-48">
                                        <form-field-textarea [(ngModel)]="scheduledDowntime.description"
                                                             name="description"
                                                             [ngRequired]="true"
                                                             [required]="true"
                                                             [label]="'Description' | translate"
                                                             rows="6">
                                        </form-field-textarea>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="mon-dialog-footer text-right">
                <button type="submit"
                        class="btn btn-border-radius btn-secondary"
                        [disabled]="scheduledForm.invalid"
                        translate>
                    Save
                </button>
            </div>
        </form>
    `,
})
export class FormUptimeScheduledDowntimeComponent implements OnInit {
    @Input({ required: true }) uptimeId!: number;
    @Input() schedule?: FormScheduleDownTimeModel;
    @Input() monDialogId?: string;

    cdRef = inject(ChangeDetectorRef);
    monDialog = inject(MonDialog);
    dateTimeService = inject(DateTimeService);
    uptimeRepoService = inject(UptimeRepoService);

    scheduledDowntime: FormScheduleDownTimeModel = {
        uptime_id: this.uptimeId,
        start_at: '',
        created_at: '',
        current_end_at: '',
        current_start_at: '',
        updated_at: '',
        description: '',
        end_at: '',
        recurring: 'no',
        register_downtime: false,
    };
    recurringOptions: { name: string, value: string }[] = [];
    startDateOptions: { initDate: Date } = { initDate: new Date() };
    endDateOptions: { initDate: Date, minDate: NgbDateStruct | null, maxDate: NgbDateStruct | null } = {
        initDate: new Date(),
        minDate: null,
        maxDate: null,
    };
    loaded = false;

    formScheduledDowntimeStartAtDate = model<Date>(new Date());
    formScheduledDowntimeStartAtTime = model<Date>(new Date());
    formScheduledDowntimeStartAt = computed(() => {
        return `${this.formScheduledDowntimeStartAtDate().toDateString()} ${this.formScheduledDowntimeStartAtTime().toTimeString()}`;
    });
    formScheduledDowntimeEndAtDate = model<Date>(new Date());
    formScheduledDowntimeEndAtTime = model<Date>(new Date());
    formScheduledDowntimeEndAt = computed(() => {
        return `${this.formScheduledDowntimeEndAtDate().toDateString()} ${this.formScheduledDowntimeEndAtTime().toTimeString()}`;
    });

    ngOnInit (): void {
        this.setupRecurringOptions();
        if (this.schedule) {
            this.scheduledDowntime = safeClone(this.schedule);
        }

        this.scheduledDowntime.register_downtime = this.scheduledDowntime.register_downtime || false;
        this.scheduledDowntime.recurring = this.scheduledDowntime.recurring || this.recurringOptions[0].value;
        this.startDateOptions = {
            initDate: this.dateTimeService.toDate(undefined),
        };

        this.endDateOptions.initDate = this.dateTimeService.addDuration(undefined, 1, 'hours');

        this.setupDateParams();
        this.onChangeRecurringOrStartDate();
        this.loaded = true;
    }

    private setupDateParams (): void {
        if (this.scheduledDowntime.start_at === undefined) {
            this.scheduledDowntime.start_at = this.dateTimeService.toDate(undefined).toISOString();
        } else {
            this.scheduledDowntime.start_at = this.dateTimeService.toDate(this.scheduledDowntime.start_at).toISOString();
        }
        if (this.scheduledDowntime.end_at === undefined) {
            this.scheduledDowntime.end_at = this.dateTimeService.addDuration(undefined, 1, 'hours').toISOString();
        } else {
            this.scheduledDowntime.end_at = this.dateTimeService.toDate(this.scheduledDowntime.end_at).toISOString();
        }

        const startAtDate = new Date(this.scheduledDowntime.start_at);
        const endAtDate = new Date(this.scheduledDowntime.end_at);

        this.formScheduledDowntimeStartAtDate.set(startAtDate);
        this.formScheduledDowntimeStartAtTime.set(startAtDate);

        this.formScheduledDowntimeEndAtDate.set(endAtDate);
        this.formScheduledDowntimeEndAtTime.set(endAtDate);
    }

    private setupRecurringOptions (): void {
        this.recurringOptions = [
            {
                name: 'None',
                value: 'no',
            },
            {
                name: 'Daily',
                value: 'daily',
            },
            {
                name: 'Weekly',
                value: 'weekly',
            },
            {
                name: 'Monthly',
                value: 'monthly',
            },
        ];
    }

    onChangeRecurringOrStartDate (): void {
        const currentEndAt = this.dateTimeService.toDate(this.formScheduledDowntimeEndAt());
        const currentStartAt = this.dateTimeService.toDate(this.formScheduledDowntimeStartAt());
        let maxDate: Date | null = this.dateTimeService.toDate(this.scheduledDowntime.start_at);

        this.endDateOptions.minDate = ngbDateStructFromNativeDate(currentStartAt);
        if (this.dateTimeService.isBefore(currentEndAt, currentStartAt)) {
            const newEndAtDate = this.dateTimeService.addDuration(currentStartAt, 1, 'hours');
            this.formScheduledDowntimeEndAtDate.set(newEndAtDate);
            this.formScheduledDowntimeEndAtTime.set(newEndAtDate);
        }

        switch (this.scheduledDowntime.recurring) {
            case 'daily':
                maxDate = this.dateTimeService.addDuration(maxDate, 24, 'hours');
                break;
            case 'weekly':
                maxDate = this.dateTimeService.addDuration(maxDate, 24, 'hours');
                maxDate = this.dateTimeService.addDuration(maxDate, 7, 'days');
                break;
            case 'monthly':
                maxDate = this.dateTimeService.addDuration(maxDate, 24, 'hours');
                maxDate = this.dateTimeService.addDuration(maxDate, 1, 'months');
                break;
            default:
                maxDate = null;
                break;
        }

        if (!maxDate) {
            this.endDateOptions.maxDate = null;
            return;
        }

        this.endDateOptions.maxDate = ngbDateStructFromNativeDate(maxDate);
        if (!this.dateTimeService.isBefore(currentEndAt, maxDate)) {
            this.formScheduledDowntimeEndAtDate.set(maxDate);
            this.formScheduledDowntimeEndAtTime.set(maxDate);
        }
    }

    async submit (): Promise<void> {
        this.scheduledDowntime.start_at = this.dateTimeService.toDate(this.formScheduledDowntimeStartAt()).toISOString();
        this.scheduledDowntime.end_at = this.dateTimeService.toDate(this.formScheduledDowntimeEndAt()).toISOString();

        try {
            if (!this.scheduledDowntime.id) {
                await this.uptimeRepoService.createScheduledDowntime(this.uptimeId, this.scheduledDowntime);
            } else {
                await this.uptimeRepoService.updateScheduledDowntime(
                    this.uptimeId,
                    this.scheduledDowntime.id,
                    this.scheduledDowntime,
                );
            }

            await this.monDialog.close(this.monDialogId, null);
        } catch (_e) {}
    }

}
